// Centralised IST (Asia/Kolkata) date/time formatting for anything shown to the user.
//
// All timestamps are stored in UTC (timestamptz / ISO 8601). The app is India-only, so
// every user-facing date must render in IST regardless of the viewer's browser timezone.
// Historically some sites used `new Date(x).toLocaleDateString()` with no timeZone option,
// which rendered in the VIEWER's local zone and could show the wrong day/time. Use these
// helpers everywhere instead so IST is forced consistently.
//
// Built on the native Intl API (no moment dependency) with an explicit Asia/Kolkata zone.

const IST = 'Asia/Kolkata';

function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** "04/08/2026" (IST) */
export function istDate(value) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', {
    timeZone: IST, day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

/** "04 Aug 2026" (IST) — friendlier medium form for public pages */
export function istDateMed(value) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', {
    timeZone: IST, day: '2-digit', month: 'short', year: 'numeric',
  });
}

/** "07:01 PM" (IST) */
export function istTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', {
    timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

/** "04/08/2026, 07:01 PM" (IST) */
export function istDateTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${istDate(d)}, ${istTime(d)}`;
}

/** "04 Aug 2026, 07:01 PM" (IST) — medium form with time */
export function istDateTimeMed(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${istDateMed(d)}, ${istTime(d)}`;
}

/**
 * "2026-08-04T19:01" in IST wall-clock, for prefilling <input type="datetime-local">.
 * The input has no timezone, so we must feed it the IST clock value (not UTC) and parse
 * it back as IST on save (see istLocalInputToISO).
 */
export function isoToISTInputValue(value) {
  const d = toDate(value);
  if (!d) return '';
  // en-CA gives YYYY-MM-DD; combine with 24h HH:mm in IST.
  const date = d.toLocaleDateString('en-CA', { timeZone: IST });
  const time = d.toLocaleTimeString('en-GB', {
    timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return `${date}T${time}`;
}

/**
 * Inverse of isoToISTInputValue: interpret a datetime-local string ("YYYY-MM-DDTHH:mm")
 * as IST wall-clock and return the corresponding UTC ISO string for storage.
 */
export function istInputValueToISO(localValue) {
  if (!localValue) return null;
  // Append the fixed IST offset so Date parses the wall-clock as IST, then serialise to UTC.
  const d = new Date(`${localValue}:00+05:30`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

const istDateUtils = {
  istDate, istDateMed, istTime, istDateTime, istDateTimeMed,
  isoToISTInputValue, istInputValueToISO,
};
export default istDateUtils;
